import axios, { AxiosError } from "axios"
import axiosRetry, { exponentialDelay, IAxiosRetryConfig, isNetworkError } from "axios-retry"
import type { OpenAPIConfig } from "../generated/core/OpenAPI"
import { ApiRequestOptions } from "../generated/core/ApiRequestOptions"
import { BaseHttpRequest } from "../generated/core/BaseHttpRequest"
import { CancelablePromise } from "../generated/core/CancelablePromise"
import { request as __request } from "../generated/core/request"

const isRetryableError = (error: AxiosError): boolean => {
  const isNotTimeoutError = error.code !== `ECONNABORTED`
  const isForbiddenError = error.response?.status === 403
  const isServerError =
    error.response?.status !== undefined && error.response.status >= 500 && error.response.status <= 599

  return isNotTimeoutError && (!error.response || isForbiddenError || isServerError)
}

const isSafeRequestError = (error: AxiosError): boolean => {
  const SAFE_HTTP_METHODS = [`get`, `head`, `options`]

  // Cannot determine if the request can be retried
  if (!error.config?.method) return false

  return isRetryableError(error) && SAFE_HTTP_METHODS.includes(error.config.method)
}

const isNetworkOrForbiddenRequestError = (error: AxiosError): boolean =>
  isNetworkError(error) || isSafeRequestError(error)

export const createAxiosRetryConfig = (options?: Partial<AxiosRetryConfig>): AxiosRetryConfig => ({
  retries: 3,
  retryCondition: isNetworkOrForbiddenRequestError,
  retryDelay: exponentialDelay,
  ...options,
})

export type AxiosRetryConfig = IAxiosRetryConfig

export const createAxiosHttpRequest = (retryConfig?: AxiosRetryConfig) =>
  class AxiosHttpRequest extends BaseHttpRequest {
    axiosInstance = axios.create()

    constructor(config: OpenAPIConfig) {
      super(config)

      axiosRetry(this.axiosInstance, retryConfig)
    }

    public override request<T>(options: ApiRequestOptions): CancelablePromise<T> {
      return __request(this.config, options, this.axiosInstance)
    }
  }
